/**
 * Parse delta changes into human-readable entity-level summaries
 */

import { Plane, Users, MessageSquare, Phone, CreditCard, Ticket, Briefcase, DollarSign, Database, Info, LucideIcon } from 'lucide-react'

export interface ChangeSummary {
  category: string
  icon: LucideIcon
  count: number
  description: string
}

interface ChangeEntry {
  from: unknown
  to: unknown
  change_type: 'value_change' | 'added' | 'removed' | 'type_change' | 'list_size_change'
  added?: unknown[]
  removed?: unknown[]
}

// Mapping of path patterns to categories
const CATEGORY_PATTERNS: Array<{
  patterns: RegExp[]
  category: string
  icon: LucideIcon
  singularLabel: string
  pluralLabel: string
}> = [
  {
    patterns: [/^data\.flightProducts/, /^data\.flights/, /^data\.segments/],
    category: 'flights',
    icon: Plane,
    singularLabel: 'Flug geändert',
    pluralLabel: 'Flüge geändert',
  },
  {
    patterns: [/^data\.travelers/, /^data\.passengers/],
    category: 'passengers',
    icon: Users,
    singularLabel: 'Passagier geändert',
    pluralLabel: 'Passagiere geändert',
  },
  {
    patterns: [/^data\.remarks/],
    category: 'remarks',
    icon: MessageSquare,
    singularLabel: 'Bemerkung geändert',
    pluralLabel: 'Bemerkungen geändert',
  },
  {
    patterns: [/^data\.contacts/, /^data\.contactInfo/],
    category: 'contacts',
    icon: Phone,
    singularLabel: 'Kontakt geändert',
    pluralLabel: 'Kontakte geändert',
  },
  {
    patterns: [/^data\.frequentFlyerCards/, /^data\.loyaltyPrograms/],
    category: 'loyalty',
    icon: CreditCard,
    singularLabel: 'Vielfliegerkarte geändert',
    pluralLabel: 'Vielfliegerkarten geändert',
  },
  {
    patterns: [/^data\.tickets/, /^data\.ticketDocuments/],
    category: 'tickets',
    icon: Ticket,
    singularLabel: 'Ticket geändert',
    pluralLabel: 'Tickets geändert',
  },
  {
    patterns: [/^data\.serviceRequests/, /SSR/, /^data\.specialServices/],
    category: 'services',
    icon: Briefcase,
    singularLabel: 'Service geändert',
    pluralLabel: 'Services geändert',
  },
  {
    patterns: [/^data\.fareFamily/, /^data\.pricing/, /^data\.fare/],
    category: 'pricing',
    icon: DollarSign,
    singularLabel: 'Preisinformation geändert',
    pluralLabel: 'Preisinformationen geändert',
  },
  {
    patterns: [/^dictionaries/],
    category: 'reference',
    icon: Database,
    singularLabel: 'Referenzdaten aktualisiert',
    pluralLabel: 'Referenzdaten aktualisiert',
  },
]

/**
 * Categorize a change path into a known category
 */
function categorizeChange(path: string): typeof CATEGORY_PATTERNS[0] | null {
  for (const category of CATEGORY_PATTERNS) {
    if (category.patterns.some(pattern => pattern.test(path))) {
      return category
    }
  }
  return null
}

/**
 * Parse the changes object from a BookingDelta and produce human-readable summaries
 *
 * @param changes - The changes object from BookingDelta.changes field
 *                  Format: { "path.to.field": { from, to, change_type }, ... }
 * @returns Array of category summaries with counts and descriptions
 */
export function parseChanges(changes: Record<string, ChangeEntry> | null | undefined): ChangeSummary[] {
  if (!changes || typeof changes !== 'object') {
    return []
  }

  // Count changes per category
  const categoryCounts: Map<string, { config: typeof CATEGORY_PATTERNS[0]; count: number }> = new Map()
  let otherCount = 0

  for (const path of Object.keys(changes)) {
    const categoryConfig = categorizeChange(path)

    if (categoryConfig) {
      const existing = categoryCounts.get(categoryConfig.category)
      if (existing) {
        existing.count++
      } else {
        categoryCounts.set(categoryConfig.category, { config: categoryConfig, count: 1 })
      }
    } else {
      otherCount++
    }
  }

  // Build summaries
  const summaries: ChangeSummary[] = []

  for (const { config, count } of categoryCounts.values()) {
    summaries.push({
      category: config.category,
      icon: config.icon,
      count,
      description: count === 1 ? config.singularLabel : `${count} ${config.pluralLabel}`,
    })
  }

  // Add "Other" category if there are uncategorized changes
  if (otherCount > 0) {
    summaries.push({
      category: 'other',
      icon: Info,
      count: otherCount,
      description: otherCount === 1 ? 'Sonstige Änderung' : `${otherCount} sonstige Änderungen`,
    })
  }

  // Sort by count descending
  summaries.sort((a, b) => b.count - a.count)

  return summaries
}

/**
 * Generate a short inline summary string (for compact displays)
 * e.g., "2 Flüge, 1 Passagier"
 */
export function generateShortSummary(changes: Record<string, ChangeEntry> | null | undefined): string {
  const summaries = parseChanges(changes)

  if (summaries.length === 0) {
    return 'Keine Details'
  }

  // Take top 2-3 categories
  const topCategories = summaries.slice(0, 3)

  const parts = topCategories.map(s => {
    // Use shorter labels for inline display
    switch (s.category) {
      case 'flights': return s.count === 1 ? '1 Flug' : `${s.count} Flüge`
      case 'passengers': return s.count === 1 ? '1 Passagier' : `${s.count} Passagiere`
      case 'remarks': return s.count === 1 ? '1 Bemerkung' : `${s.count} Bemerkungen`
      case 'contacts': return s.count === 1 ? '1 Kontakt' : `${s.count} Kontakte`
      case 'loyalty': return s.count === 1 ? '1 Vielfliegerkarte' : `${s.count} Vielfliegerkarten`
      case 'tickets': return s.count === 1 ? '1 Ticket' : `${s.count} Tickets`
      case 'services': return s.count === 1 ? '1 Service' : `${s.count} Services`
      case 'pricing': return s.count === 1 ? '1 Preis' : `${s.count} Preise`
      case 'reference': return 'Referenzdaten'
      default: return s.count === 1 ? '1 Änderung' : `${s.count} Änderungen`
    }
  })

  return parts.join(', ')
}
